import { useState } from 'react';
import { Search, Check, Palette, Building2, Leaf, Bitcoin, Crown, Eye, X, Monitor, Tablet, Smartphone } from 'lucide-react';
import { useThemeTemplates, useTemplateCategories, useApplyTemplate, ThemeTemplate } from '../../hooks/useThemeTemplates';
import { ThemePreviewEngine } from './ThemePreviewEngine';

interface TemplateGalleryProps {
  tenantId: string;
  appliedTemplateId?: string | null;
  onTemplateSelect?: (template: ThemeTemplate) => void;
  onTemplateApply?: (template: ThemeTemplate) => void;
}

type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

const categoryIcons: Record<string, React.ReactNode> = {
  FINTECH: <Palette className="w-4 h-4" />,
  BANKING: <Building2 className="w-4 h-4" />,
  AGRO: <Leaf className="w-4 h-4" />,
  CRYPTO: <Bitcoin className="w-4 h-4" />,
  PREMIUM: <Crown className="w-4 h-4" />,
};

const categoryLabels: Record<string, string> = {
  FINTECH: 'Fintech',
  BANKING: 'Banking',
  AGRO: 'Agro',
  CRYPTO: 'Crypto',
  PREMIUM: 'Premium',
};

// Modal de Preview Visual Completo
function TemplatePreviewModal({
  template,
  onClose,
  onApply,
  isApplying,
}: {
  template: ThemeTemplate;
  onClose: () => void;
  onApply: () => void;
  isApplying: boolean;
}) {
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  
  const deviceWidths: Record<PreviewDevice, string> = {
    desktop: 'max-w-4xl',
    tablet: 'max-w-2xl',
    mobile: 'max-w-sm',
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface-900 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-surface-700 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-700">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, rgb(${template.primaryColors['500']}), rgb(${template.primaryColors['700']}))`,
              }}
            />
            <div>
              <h2 className="text-xl font-bold text-white">{template.name}</h2>
              <p className="text-sm text-surface-400">{template.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Device Selector */}
        <div className="flex items-center justify-center gap-2 p-3 bg-surface-800/50 border-b border-surface-700">
          <span className="text-sm text-surface-400 mr-2">Visualizar em:</span>
          <button
            onClick={() => setPreviewDevice('desktop')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              previewDevice === 'desktop' 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
            }`}
          >
            <Monitor className="w-4 h-4" />
            Desktop
          </button>
          <button
            onClick={() => setPreviewDevice('tablet')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              previewDevice === 'tablet' 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
            }`}
          >
            <Tablet className="w-4 h-4" />
            Tablet
          </button>
          <button
            onClick={() => setPreviewDevice('mobile')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              previewDevice === 'mobile' 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
            }`}
          >
            <Smartphone className="w-4 h-4" />
            Mobile
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto p-6 bg-surface-950">
          <div className={`mx-auto transition-all duration-300 ${deviceWidths[previewDevice]}`}>
            <ThemePreviewEngine
              theme={{
                primaryColors: template.primaryColors as any,
                accentColors: template.accentColors as any,
                surfaceColors: template.surfaceColors as any,
                layoutType: template.layoutType,
                buttonVariant: template.buttonVariant as any || 'solid',
                cardVariant: template.cardVariant as any || 'elevated',
                tableVariant: template.tableVariant as any || 'striped',
                fontFamily: template.fontFamily || 'Inter',
                borderRadius: template.borderRadius || '0.5rem',
              }}
              mode="dark"
            />
          </div>
        </div>

        {/* Template Details */}
        <div className="p-4 bg-surface-800 border-t border-surface-700">
          <div className="flex items-center justify-between">
            <div className="flex gap-6">
              <div>
                <span className="text-xs text-surface-500 block mb-1">Layout</span>
                <span className="text-sm text-white bg-surface-700 px-2 py-1 rounded">{template.layoutType}</span>
              </div>
              <div>
                <span className="text-xs text-surface-500 block mb-1">Botões</span>
                <span className="text-sm text-white bg-surface-700 px-2 py-1 rounded">{template.buttonStyle || 'solid'}</span>
              </div>
              <div>
                <span className="text-xs text-surface-500 block mb-1">Categoria</span>
                <span className="text-sm text-white bg-surface-700 px-2 py-1 rounded flex items-center gap-1">
                  {template.category && categoryIcons[template.category]}
                  {template.category ? categoryLabels[template.category] : 'Geral'}
                </span>
              </div>
              <div>
                <span className="text-xs text-surface-500 block mb-1">Cores Primárias</span>
                <div className="flex gap-0.5">
                  {['400', '500', '600', '700'].map((shade) => (
                    <div
                      key={shade}
                      className="w-6 h-6 rounded"
                      style={{ backgroundColor: `rgb(${template.primaryColors[shade]})` }}
                      title={`Primary ${shade}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-surface-700 text-white rounded-lg hover:bg-surface-600 transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={onApply}
                disabled={isApplying}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isApplying ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Aplicando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Aplicar Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TemplateGallery({
  tenantId,
  appliedTemplateId,
  onTemplateSelect,
  onTemplateApply,
}: TemplateGalleryProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<ThemeTemplate | null>(null);

  const { data: templates, isLoading } = useThemeTemplates(selectedCategory);
  const { data: categories } = useTemplateCategories();
  const applyTemplate = useApplyTemplate();

  const filteredTemplates = templates?.filter((template) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query)
    );
  });

  const handleApply = async (template: ThemeTemplate) => {
    try {
      await applyTemplate.mutateAsync({ tenantId, templateId: template.id });
      onTemplateApply?.(template);
      setPreviewTemplate(null); // Fecha o modal após aplicar
    } catch (error) {
      console.error('Failed to apply template:', error);
    }
  };

  const handlePreview = (template: ThemeTemplate) => {
    setPreviewTemplate(template);
  };

  const renderColorPreview = (colors: Record<string, string>) => {
    const shades = ['500', '600', '700'];
    return (
      <div className="flex gap-0.5">
        {shades.map((shade) => (
          <div
            key={shade}
            className="w-4 h-4 rounded-sm"
            style={{ backgroundColor: `rgb(${colors[shade]})` }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory(undefined)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              !selectedCategory
                ? 'bg-primary-600 text-white'
                : 'bg-surface-800 text-gray-300 hover:bg-surface-700'
            }`}
          >
            Todos
          </button>
          {categories?.map((cat) => (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                selectedCategory === cat.category
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-800 text-gray-300 hover:bg-surface-700'
              }`}
            >
              {categoryIcons[cat.category]}
              {categoryLabels[cat.category]} ({cat.count})
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="h-48 bg-surface-800 rounded-xl animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTemplates?.map((template) => (
            <div
              key={template.id}
              className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                appliedTemplateId === template.id
                  ? 'border-primary-500 ring-2 ring-primary-500/30'
                  : 'border-surface-700 hover:border-surface-600'
              }`}
              onMouseEnter={() => setHoveredTemplate(template.id)}
              onMouseLeave={() => setHoveredTemplate(null)}
              onClick={() => onTemplateSelect?.(template)}
            >
              {/* Color Preview Header */}
              <div
                className="h-24 relative"
                style={{
                  background: `linear-gradient(135deg, rgb(${template.primaryColors['500']}), rgb(${template.primaryColors['700']}))`,
                }}
              >
                {/* Applied Badge */}
                {appliedTemplateId === template.id && (
                  <div className="absolute top-2 right-2 bg-primary-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Aplicado
                  </div>
                )}

                {/* Category Badge */}
                {template.category && (
                  <div className="absolute top-2 left-2 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    {categoryIcons[template.category]}
                    {categoryLabels[template.category]}
                  </div>
                )}

                {/* Default Badge */}
                {template.isDefault && (
                  <div className="absolute bottom-2 left-2 bg-accent-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Padrão
                  </div>
                )}
              </div>

              {/* Template Info */}
              <div className="p-4 bg-surface-800">
                <h3 className="font-semibold text-white mb-1">{template.name}</h3>
                <p className="text-sm text-gray-400 line-clamp-2 mb-3">
                  {template.description}
                </p>

                {/* Color Swatches */}
                <div className="flex gap-4">
                  <div>
                    <span className="text-xs text-gray-500 block mb-1">Primary</span>
                    {renderColorPreview(template.primaryColors)}
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block mb-1">Accent</span>
                    {renderColorPreview(template.accentColors)}
                  </div>
                </div>

                {/* Layout & Style Info */}
                <div className="mt-3 flex gap-2 flex-wrap">
                  <span className="text-xs bg-surface-700 text-gray-300 px-2 py-1 rounded">
                    {template.layoutType}
                  </span>
                  <span className="text-xs bg-surface-700 text-gray-300 px-2 py-1 rounded">
                    {template.buttonStyle}
                  </span>
                </div>
              </div>

              {/* Hover Actions */}
              {hoveredTemplate === template.id && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center gap-3 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreview(template);
                    }}
                    className="px-4 py-2 bg-surface-700 text-white rounded-lg hover:bg-surface-600 transition-colors flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApply(template);
                    }}
                    disabled={applyTemplate.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    {applyTemplate.isPending ? 'Aplicando...' : 'Aplicar'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {filteredTemplates?.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Palette className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Nenhum template encontrado
          </h3>
          <p className="text-gray-400">
            Tente ajustar os filtros ou a busca
          </p>
        </div>
      )}

      {/* Modal de Preview */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onApply={() => handleApply(previewTemplate)}
          isApplying={applyTemplate.isPending}
        />
      )}
    </div>
  );
}

export default TemplateGallery;
